import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'universal-cookie';

import CartTotals from './cartTotals';
import CouponField from './couponField';

import { pricingFormat } from '../../../services/productItemsService';

import './cartItems.css'

/**
 * CartItems.
 * Displays the cart items for the Cart Page.
 *
 * @author  Manny
 * @param   {Object}      props.cart                  Cart Object.
 * @param   {Object}      props.cartQuotes            CartQuotes Object.
 * @param   {Object}      props.coupon                FormError Object containing form error properties in boolean.
 * @param   {String}      props.couponCode            Coupon code text.
 * @param   {Boolean}     props.isCouponValidated     Flag for confirming if coupon has been validated.
 * @param   {Boolean}     props.loading               Loader boolean to hide/show spinner.
 * @param   {Function}    props.onCartLoad            Function from the Cart Page to refresh the cart, delete a past order.
 * @param   {Function}    props.onCheckout            Function from the Cart Page to set Customer details.
 * @param   {Function}    props.onCouponApply         Function from the Cart Page to to apply a coupon code.
 * @param   {Function}    props.onCouponInputChange   Function from the Cart Page to handle coupon code onInputChange.
 * @param   {Function}    props.onCustomiseProduct    Function from the Cart Page, calls updateQuoteConfig.
 * @param   {Function}    props.onDeleteCoupon        Function from the Cart Page to to remove a coupon code.
 * @param   {Function}    props.onDeleteProduct       Function from the Cart Page to delete a cart item.
 * @param   {Object}      props.order                 Order Object from redux store.
 * @param   {Function}    props.trackGenericPageInfo  Function from the segmentAnalytics service.
 * @returns {JSX.Element}                             The component markup.
 */

const CartItems = ({
  cart,
  cartQuotes,
  coupon,
  couponCode,
  isCouponValidated,
  loading,
  onCartLoad,
  onCheckout,
  onCouponApply,
  onCouponInputChange,
  onCustomiseProduct,
  onDeleteCoupon,
  onDeleteProduct,
  order,
  trackGenericPageInfo
}) => {
  const [currentCart, setCurrentCart] = useState(null);
  const [currentCartQuotes, setCurrentCartQuotes] = useState(null);
  const [totalMonths, setTotalMonths] = useState(0);
  const [selectedGeography, setSelectedGeography] = useState('');

  /* istanbul ignore next */
  const setCartItems = (newCart, newCartQuotes) => {
    let newTotalMonths = 0;
    let config = {};
    let newSelectedGeography = '';
    const cartItems = newCart?.cartItems || null;
    let quotes = newCartQuotes?.quotes || null;
    if (newCart && Object.keys(newCart).length > 0 && cartItems && quotes) {
      quotes.map((quote) => {
        quote.deedMessage = false;
        quote.contractDays = quotes.totalPerMonth === 0 ? 0 : 90;
        cartItems.map((cartItem) => {
          config = JSON.parse(cartItem.configuration);
          let { configuration } = config;
          let { geography, industry, package: configPackage, packageName } = configuration;
          let { states, national } = geography || {};
          if (cartItem.productId === quote.productId) {
            quote.cartItemId = cartItem.cartItemId;
            quote.customise = true;
            if (quote.originalPrice === 0) quote.customise = false;
            else if (quote.productGroup === 'Report Subscription') {
              let cartInfo = setCartInfoForReportSubscription(packageName, states);
              quote.customise = cartInfo.customise;
              newSelectedGeography = cartInfo.selectedGeography;
            } else if (quote.type === 'Blockbrief') {
              quote.customise = false;
            } else if (quote.type === 'RPPRealEstate') {
              quote = setCartInfoForRPP(configPackage, industry, national, quote, states);
            }
            quote.contractMonths =
              quote.minimumInitialTerm + (quote.provisioningInfo?.discountPeriods || 0);
            if (newTotalMonths === 0 || newTotalMonths < quote.contractMonths)
              newTotalMonths = quote.contractMonths;
            quote.configuration = config;
          }
        });
      });
    }
    setCurrentCart(newCart);
    setCurrentCartQuotes(newCartQuotes);
    setTotalMonths(newTotalMonths);
    setSelectedGeography(newSelectedGeography);
  };

  /* istanbul ignore next */
  const setCartInfoForReportSubscription = (packageName, state = null) => {
    let cartInfo = {};
    if (packageName !== 'investors_premium') {
      cartInfo.customise = false;
    } else if (state) {
      // Add selected geography to state for investors premium
      cartInfo.selectedGeography = state[0];
    }
    return cartInfo;
  };

  /* istanbul ignore next */
  const setCartInfoForRPP = (configPackage, industry, national, quote, states = null) => {
    const cookies = new Cookies();
    cookies.set('industry', industry);
    if (
      industry === 'real_estate_agencies_and_valuers' && (configPackage !== 'premium1UN' || configPackage !== 'premium1UNNAB') &&
      (national === true || states?.find((state) => state === 'VIC'))
    ) quote.deedMessage = true;
    if(
      industry === 'real_estate_agencies_and_valuers' && (configPackage === 'premium1UN' || configPackage === 'premium1UNNAB')
    )
      quote.customise = false;
    if (industry === 'investors' && (configPackage === 'basic' || configPackage === 'one_month'))
      quote.contractDays = 30;
    if (industry === 'property_developers_trades_services' && configPackage === 'basic1UN')
      quote.customise = false;
    return quote;
  };

  /* istanbul ignore next */
  const continueShopping = (link) => {
    trackGenericPageInfo('Continue Shopping');
    window.location.assign(link);
  };

  /* istanbul ignore next */
  useEffect(() => {
    if (cart && order && Object.keys(order).length > 0) {
      onCartLoad();
    } else {
      setCartItems(cart, cartQuotes);
    }
  }, []);

  /* istanbul ignore next */
  useEffect(() => {
    setCartItems(cart, cartQuotes);
  }, [cart, cartQuotes]);

  return (
    <div className='off-white-bg'>
      <div className='container'>
        <div className='row no-margin'>
          <div className='col-sm-12' style={{padding: '0px'}}>
            <div className='row'>
              <div id="cart_header" className='cart-header cart-h3'>Your Cart</div>
            </div>
            {currentCartQuotes === null && (
              <div className='row white-bg cart-container margin-bottom-100'>
                <br />
                <div className='row spinner'>
                  <i className='fa fa-spinner fa-pulse fa-3x fa-fw'></i>
                  <span className='sr-only'>Loading...</span>
                </div>
              </div>
            )}
            {currentCartQuotes &&
              (Object.keys(currentCartQuotes).length === 0 ||
                currentCartQuotes.quotes.length === 0) && (
                <div className='row white-bg cart-container margin-bottom-100 empty-cart-alignment'>
                  <br />
                  <div className='cart-h5'> You have no items in your cart</div>
                  <div className='body'>Click the button below to start shopping</div>
                  <a
                    id='btn_continue_shopping'
                    type='button'
                    className='btn cont-btn cart-items-btn'
                    onClick={() => {
                      continueShopping('https://www.corestore-au.cotality.com');
                    }}
                  >
                    Continue Shopping
                  </a>
                </div>
              )}
            {currentCartQuotes &&
              Object.keys(currentCartQuotes).length > 0 &&
              currentCartQuotes.quotes.length > 0 && (
                <div className='row white-bg cart-container margin-bottom-100'>
                  {coupon && isCouponValidated && Object.keys(coupon).length > 0 && coupon.blueBoxText !== null && (
                    <div className='row' style={{ marginRight: '-20px', marginLeft: '-20px' }}>
                      <div className='col-sm-12 discount-alert blue-bg captions-semibold'>
                        <span id='coupon_notification'>{coupon.blueBoxText}</span>
                      </div>
                    </div>
                  )}
                  {(coupon === null || !isCouponValidated ||
                    Object.keys(coupon).length === 0 ||
                    coupon.blueBoxText === null) && <br />}
                  <div className='col-sm-12 no-padding'>
                    {currentCartQuotes.quotes.map(function (item, index) {
                      return (
                        <div key={index}>
                          <div className='row no-margin'>
                            <div className='col-sm-12 no-padding'>
                              <div className='row no-margin'>
                                <div className='col-xs-8 no-padding'>
                                  <div
                                    id={`cart_item_headline_${index}`}
                                    className='cart-item-header cart-h5'
                                  >
                                    {item?.headLine}
                                  </div>
                                  <div>
                                    <p className='no-margin wcag-grey-text captions'>
                                      <b>Inclusions</b>
                                    </p>
                                    {item?.lineItems?.length > 0 && (
                                      <div className='checkout-item'>
                                        {item?.lineItems?.map(function (lineItem, subIndex) {
                                          return (
                                            <span key={`inclusion-${subIndex}`}>
                                              <span>{lineItem.description}</span>
                                              {subIndex < item.lineItems.length - 1 && (
                                                <span>,&nbsp;</span>
                                              )}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                  <div className='checkout-item'>
                                    {item?.features?.map(function (line, subIndex) {
                                      return (
                                        <span key={`features-${subIndex}`}>
                                          <span>
                                            {line.description.includes('Top Performing Suburbs')
                                              ? `${line.description} (${selectedGeography})`
                                              : line.description}
                                          </span>
                                          {subIndex < item.features.length - 1 && (
                                            <span>,&nbsp;</span>
                                          )}
                                        </span>
                                      );
                                    })}
                                  </div>
                                  {item?.addons?.length > 0 && (
                                    <>
                                      <br />
                                      <p className='no-margin grey-text'>
                                        <b>{item.addons.length} Add Ons </b>
                                      </p>
                                      <div className='checkout-item'>
                                        {item.addons.map(function (lineItem, subIndex) {
                                          return (
                                            <div key={`add-on-${subIndex}`}>
                                              {lineItem.description}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </>
                                  )}
                                  {item?.deedMessage && (
                                    <div className='footnote-text'>
                                      Victorian Legislation requires Real Estate Agents and Valuers
                                      to fill out a Deed 4 to be able to access Owners Details. You
                                      will receive the form and instructions attached to your
                                      confirmation email.
                                    </div>
                                  )}
                                  <br />
                                </div>
                                <div className='col-xs-4 no-padding'>
                                  <div
                                    id={`cart_item_price_${index}`}
                                    className='cart-item-header text-right cart-h5'
                                  >
                                    <span > $AUD </span>
                                    {pricingFormat(item.originalPrice.toFixed(2))}
                                    <div className='font-10 text-right captions'>Per month</div>
                                  </div>
                                  <div className='cart-items text-right footnote-text'>
                                    {item?.originalPrice === 0 && (
                                      <span>
                                        <small id={`cart_item_min_cost_${index}`}>
                                          Min. cost for Trial Period
                                        </small>
                                      </span>
                                    )}
                                    {item?.originalPrice > 0 && (
                                      <span>
                                        <small id={`cart_item_min_cost_${index}`}>
                                          Min. cost $
                                          {pricingFormat(
                                            (item.originalPrice * item.minimumInitialTerm).toFixed(
                                              2
                                            )
                                          )}{' '}
                                          for&nbsp;
                                          {item.contractMonths} month(s).
                                        </small>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className='row no-margin'>
                                <div className='col-xs-12 no-padding'>
                                  {item?.customise && (
                                    <button
                                      id={`btn_customise_cart_item_${index}`}
                                      type='button'
                                      className='btn btn-outline margin-right-5 cart-btn'
                                      onClick={() => {
                                        onCustomiseProduct(item.productId, item.configuration);
                                      }}
                                    >
                                      Customise
                                    </button>
                                  )}
                                  <a
                                    id={`btn_delete_cart_item_${index}`}
                                    type='text'
                                    className='btn btn-outline cart-btn'
                                    onClick={() => {
                                      onDeleteProduct(item.cartItemId, item.productId, item);
                                    }}
                                  >
                                    Delete
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                          <hr />
                        </div>
                      );
                    }, this)}

                    {currentCart !== null &&
                      Object.keys(currentCart).length > 0 &&
                      currentCart.cartItems.length > 0 && (
                        <div className='row no-margin'>
                          <div className='col-sm-8 terms-cond'></div>
                          <div className='col-sm-4 no-padding'>
                            <CouponField
                              coupon={couponCode}
                              couponEntity={isCouponValidated ? coupon : null}
                              monthsDiscount={
                                coupon && coupon.discountServiceConfig
                                  ? coupon.discountServiceConfig.months_discount
                                  : 0
                              }
                              onCouponApply={onCouponApply}
                              onCouponInputChange={onCouponInputChange}
                              onDeleteCoupon={onDeleteCoupon}
                            />
                            <hr />
                            <CartTotals
                              cartQuotes={currentCartQuotes}
                              couponEntity={coupon}
                              isCouponValidated={isCouponValidated}
                              pricingFormat={pricingFormat}
                              totalMonths={totalMonths}
                            />
                          </div>
                        </div>
                      )}
                    <div className='row no-margin'>
                      <hr />
                    </div>
                    {currentCart !== null &&
                      Object.keys(currentCart).length > 0 &&
                      currentCart.cartItems.length > 0 && (
                        <div className='row no-margin'>
                          {coupon?.code === 'UEMTQT' && !isCouponValidated &&currentCart.cartItems.length > 1 && (
                            <div className='col-sm-12 text-right no-padding'>
                              <div className='success-text captions' >
                                You have 2 or more products in your cart. A 10% discount will apply
                                at checkout!
                              </div>
                            </div>
                          )}
                          <div className='col-xs-12 no-padding'>
                            <button
                              id='btn_checkout'
                              type='button'
                              className={loading ? 'btn checkout-btn disabled cart-items-btn' : 'btn checkout-btn cart-items-btn'}
                              onClick={() => {
                                onCheckout();
                              }}
                            >
                              Checkout
                              {loading && (
                                <span>
                                  <i className='fa fa-spinner fa-pulse fa-fw'></i>
                                  <span className='sr-only'>Loading...</span>
                                </span>
                              )}
                            </button>
                            <a
                              id='btn_continue_shopping'
                              type='button'
                              className='btn cont-btn cart-items-btn'
                              onClick={() => {
                                continueShopping('https://www.corestore-au.cotality.com');
                              }}
                            >
                              Continue Shopping
                            </a>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

CartItems.propTypes = {
  cart: PropTypes.object.isRequired,
  cartQuotes: PropTypes.object.isRequired,
  coupon: PropTypes.object,
  couponCode: PropTypes.string,
  isCouponValidated: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onCartLoad: PropTypes.func.isRequired,
  onCheckout: PropTypes.func.isRequired,
  onCouponApply: PropTypes.func.isRequired,
  onCouponInputChange: PropTypes.func.isRequired,
  onCustomiseProduct: PropTypes.func.isRequired,
  onDeleteCoupon: PropTypes.func.isRequired,
  onDeleteProduct: PropTypes.func.isRequired,
  order: PropTypes.object.isRequired,
  trackGenericPageInfo: PropTypes.func.isRequired
};

export default CartItems;
